import { BaseHost } from "@core/shared/office-hosts/base-host";
import { IOfficeHost } from "@core/shared/office-hosts/office-host.types";
import { logger } from "@services/logger/logger.service";
import * as cmmn from '@utils/index';

export class OneNoteHost extends BaseHost implements IOfficeHost
{
    public override checkSelectable() { return true; }
    public override checkDocumentReadable(): boolean { return true; }
    public override checkEditable(): boolean { return true; }

    public override getTag(): string
    {
        return 'onenote';
    }

    public override async getSelectedContentAsync(): Promise<string>
    {
        let resultStr = '';

        const resultValue = await cmmn.callAsync<boolean>(async (r) =>
        {
            try
            {
                Office.context.document.getSelectedDataAsync(
                    Office.CoercionType.Text,
                    { valueFormat: "unformatted" },
                    (asyncResult: Office.AsyncResult<string>) =>
                    {
                        if (asyncResult.status === Office.AsyncResultStatus.Failed)
                        {
                            const error = asyncResult.error;
                            logger.error(`getSelectedContentAsync() > failed: ${ error.message }`);
                            r.next(false);
                        }
                        else
                        {
                            resultStr = asyncResult.value;
                            r.next(true);
                        }
                    }
                );
            }
            catch (err)
            {
                r.next(false);
            }
        });

        if (resultValue)
        {
            return resultStr;
        }

        return '';
    }

    public override async getDocumentTextAsync(): Promise<string>
    {
        try
        {
            let docText = '';

            await OneNote.run(async (context) =>
            {
                const page = context.application.getActivePage();
                const pageContents = page.contents;
                pageContents.load('type');
                await context.sync();

                const paragraphCollections = pageContents.items
                    .filter(content => content.type === 'Outline')
                    .map(content =>
                    {
                        const paragraphs = content.outline.paragraphs;
                        paragraphs.load('type');
                        return paragraphs;
                    });

                if (!paragraphCollections.length)
                {
                    return;
                }
                await context.sync();

                const richTexts: OneNote.RichText[] = [];
                for (const paragraphs of paragraphCollections)
                {
                    for (const paragraph of paragraphs.items)
                    {
                        if (paragraph.type === 'RichText')
                        {
                            const richText = paragraph.richText;
                            richText.load('text');
                            richTexts.push(richText);
                        }
                    }
                }
                await context.sync();

                docText = richTexts
                    .map(richText => richText.text)
                    .filter(Boolean)
                    .join('\n');
            });

            return docText;
        }
        catch (err)
        {
            logger.error(`getDocumentTextAsync() > failed: ${ err }`);
            return '';
        }
    }

    public override async getDocumentIDAsync(): Promise<string>
    {
        try
        {
            let pageId = '';
            let pageTitle = '';

            await OneNote.run(async (context) =>
            {
                const page = context.application.getActivePage();
                page.load('id,title');
                await context.sync();
                pageId = page.id;
                pageTitle = page.title;
            });

            logger.info(`getDocumentIDAsync() > active page: "${ pageTitle }" (${ pageId })`);

            if (pageId)
            {
                return `onenote-${ pageId }`;
            }
        }
        catch (err)
        {
            logger.error(`getDocumentIDAsync() > failed to get active page id: ${ err }`);
        }

        return 'oneNote';
    }

    public override async setSelectedTextAsync(plainText: string): Promise<void>
    {
        Office.context.document.setSelectedDataAsync(
            plainText,
            {
                coercionType: Office.CoercionType.Text
            },
        );
    }

    public override async setSelectedFormattedTextAsync(plainText: string, html: string): Promise<void>
    {
        await OneNote.run(async (context) =>
        {
            context.application.insertHtmlAtCurrentPosition(html);
            await context.sync();
        });
    }
}
